/**
 * SceneExecutor — flat v2 scenario execution (1 block = 1 scene).
 */

import { normalizeCharacterCommandTarget } from './character-runtime';
import { calculateScenarioProgress } from './scenario-progress';
import {
  findLocatedScene,
  getSceneById,
  isChoiceScene,
  isCommandScene,
  resolveChoiceEntry,
  resolveImplicitNextSceneId,
  resolveNextSceneId,
  resolvePhoneStateTarget,
  sceneConditionIsMet,
  syncPhoneState,
} from './scene-flow';
import type {
  Scene,
  CommandItem,
  CommandScene,
  ChoiceOption,
  ChoiceScene,
  ClickableScreenZone,
  GameState,
  Scenario,
  TextOverlayScene,
  ChapterEndScene,
  ConfidenceGainScene,
  PhoneChatScene,
  PhoneCallScene,
  PhoneMailScene,
  PhoneMailInteractScene,
  PasswordGameScene,
  DragDropGameScene,
  SituationSortGameScene,
  TimedOverlayScene,
  DialogueScene,
  GameOverScene,
  RecordCardScene,
} from '../types';
import { resolveChapterEnd } from './chapter-end-content';
import { reconcileObservationFlags, resetObservationState } from './object-inspect-utils';

export interface SceneProgress {
  currentIndex: number;
  totalItems: number;
  percentage: number;
}

export class SceneExecutor {
  static getSceneById(scenario: Scenario, sceneId: string): Scene | null {
    return getSceneById(scenario, sceneId);
  }

  static getCurrentScene(scenario: Scenario, gameState: GameState): Scene | null {
    return getSceneById(scenario, gameState.currentSceneId);
  }

  /** @deprecated Use getCurrentScene — each scene is one block. */
  static getCurrentContent(scene: Scene | null, _dialogueIndex?: number): Scene | null {
    return scene;
  }

  static isDialogue(scene: Scene | null): scene is DialogueScene {
    return scene?.type === 'dialogue';
  }

  static isCommand(scene: Scene | null): scene is CommandScene {
    return isCommandScene(scene);
  }

  static isChoice(scene: Scene | null): scene is ChoiceScene {
    return isChoiceScene(scene);
  }

  static isTextOverlay(scene: Scene | null): scene is TextOverlayScene {
    return scene?.type === 'textOverlay';
  }

  static isChapterEnd(scene: Scene | null): scene is ChapterEndScene {
    return scene?.type === 'chapterEnd';
  }

  static isChapterEndPresentation(scene: Scene | null): boolean {
    return resolveChapterEnd(scene) !== null;
  }

  static isConfidenceGain(scene: Scene | null): scene is ConfidenceGainScene {
    return scene?.type === 'confidenceGain';
  }

  static isPhoneChat(scene: Scene | null): scene is PhoneChatScene {
    return scene?.type === 'phoneChat';
  }

  static isPhoneCall(scene: Scene | null): scene is PhoneCallScene {
    return scene?.type === 'phoneCall';
  }

  static isPhoneMail(scene: Scene | null): scene is PhoneMailScene {
    return scene?.type === 'phoneMail';
  }

  static isPhoneMailInteract(scene: Scene | null): scene is PhoneMailInteractScene {
    return scene?.type === 'phoneMailInteract';
  }

  static isPasswordGame(scene: Scene | null): scene is PasswordGameScene {
    return scene?.type === 'passwordGame';
  }

  static isDragDropGame(scene: Scene | null): scene is DragDropGameScene {
    return scene?.type === 'dragDropGame';
  }

  static isSituationSortGame(scene: Scene | null): scene is SituationSortGameScene {
    return scene?.type === 'situationSortGame';
  }

  static isTimedOverlay(scene: Scene | null): scene is TimedOverlayScene {
    return scene?.type === 'timedOverlay';
  }

  static isGameOver(scene: Scene | null): scene is GameOverScene {
    return scene?.type === 'gameOver';
  }

  static isRecordCard(scene: Scene | null): scene is RecordCardScene {
    return scene?.type === 'recordCard';
  }

  static isObservation(scene: Scene | null): boolean {
    return scene?.type === 'observation';
  }

  static isClickableScreen(scene: Scene | null): boolean {
    return scene?.type === 'clickableScreen';
  }

  static processClickableZone(
    scenario: Scenario,
    gameState: GameState,
    zone: ClickableScreenZone,
    sceneId: string,
  ): { gameState: GameState; scene: Scene } | { error: string } {
    const option: ChoiceOption = {
      text: zone.label,
      goto: zone.goto,
      actions: zone.actions,
      pedaShields: zone.pedaShields,
      pedaCategory: zone.pedaCategory,
      confidenceGains: zone.confidenceGains,
    };
    return SceneExecutor.processChoice(scenario, gameState, option, sceneId);
  }

  static executeCommand(command: CommandItem | CommandScene, gameState: GameState): GameState {
    const newState = { ...gameState };
    const currentHidden = newState.hiddenElements || new Set();

    switch (command.command) {
      case 'setFlag':
        newState.flags = { ...newState.flags, [command.target]: command.value as boolean };
        break;
      case 'setVar':
        if (command.target === 'phoneState') break;
        newState.variables = { ...newState.variables, [command.target]: command.value as number };
        break;
      case 'collectItem':
        if (!newState.inventory.includes(command.target)) {
          newState.inventory = [...newState.inventory, command.target];
        }
        break;
      case 'unlockSkill':
        if (!newState.unlockedSkills.includes(command.target)) {
          newState.unlockedSkills = [...newState.unlockedSkills, command.target];
        }
        break;
      case 'unlockAchievement':
        if (!newState.unlockedAchievements.includes(command.target)) {
          newState.unlockedAchievements = [...newState.unlockedAchievements, command.target];
          newState.isAchievementAnimating = true;
          newState.lastUnlockedAchievementId = command.target;
        }
        break;
      case 'showElement': {
        const target = normalizeCharacterCommandTarget(command.target);
        if (currentHidden.has(target)) {
          newState.hiddenElements = new Set([...currentHidden].filter((id) => id !== target));
        }
        break;
      }
      case 'hideElement': {
        const target = normalizeCharacterCommandTarget(command.target);
        if (!currentHidden.has(target)) {
          newState.hiddenElements = new Set([...currentHidden, target]);
        }
        break;
      }
      default:
        console.warn('Unknown command:', command.command);
    }
    return newState;
  }

  static executeCommands(commands: Array<CommandItem | CommandScene>, gameState: GameState): GameState {
    let currentState = gameState;
    for (const command of commands) {
      currentState = SceneExecutor.executeCommand(command, currentState);
    }
    return currentState;
  }

  static applyLeadingCommands(scenario: Scenario, gameState: GameState): GameState {
    let state = { ...gameState };
    let safety = 0;
    while (safety++ < 100) {
      const scene = getSceneById(scenario, state.currentSceneId);
      if (!scene) break;

      if (!sceneConditionIsMet(scene, state)) {
        const next = resolveImplicitNextSceneId(scenario, state.currentSceneId);
        if (!next || next === state.currentSceneId) break;
        state = { ...state, currentSceneId: next };
        continue;
      }

      if (isCommandScene(scene)) {
        state = SceneExecutor.executeCommand(scene, state);
        const next = resolveNextSceneId(scenario, state.currentSceneId);
        if (!next || next === state.currentSceneId) break;
        state = { ...state, currentSceneId: next };
        continue;
      }

      if (scene.type === 'dialogue' && scene.actions?.length) {
        state = SceneExecutor.executeCommands(scene.actions, state);
      }
      break;
    }
    // Si on atterrit sur une scène d'observation dont le puzzle vient d'être
    // réinitialisé (replay, vieille sauvegarde), on purge aussi les flags
    // moteur `inspected_` / `reported_` pour que les objets soient examinables.
    const landedScene = getSceneById(scenario, state.currentSceneId);
    const resetFlags = resetObservationState(landedScene, state.flags);
    if (resetFlags) {
      state = { ...state, flags: resetFlags };
    }
    const reconciledFlags = reconcileObservationFlags(landedScene, state.flags);
    if (reconciledFlags) {
      state = { ...state, flags: reconciledFlags };
    }
    return syncPhoneState(scenario, state);
  }

  static navigateToScene(
    scenario: Scenario,
    gameState: GameState,
    sceneId: string
  ): { gameState: GameState; scene: Scene } | { error: string } {
    const targetScene = getSceneById(scenario, sceneId);
    if (!targetScene) {
      return { error: `Scene not found: ${sceneId}` };
    }

    const newVisitedScenes = new Set(gameState.visitedScenes);
    newVisitedScenes.add(sceneId);

    let newGameState: GameState = {
      ...gameState,
      currentSceneId: sceneId,
      visitedScenes: newVisitedScenes,
      hiddenElements: new Set(),
    };

    newGameState = SceneExecutor.applyLeadingCommands(scenario, newGameState);
    const finalScene = getSceneById(scenario, newGameState.currentSceneId);
    if (!finalScene) {
      return { error: `Scene not found after commands: ${newGameState.currentSceneId}` };
    }

    return { gameState: newGameState, scene: finalScene };
  }

  static processChoice(
    scenario: Scenario,
    gameState: GameState,
    option: ChoiceOption,
    choiceSceneId: string
  ): { gameState: GameState; scene: Scene } | { error: string } {
    let currentState = gameState;
    if (option.actions?.length) {
      currentState = SceneExecutor.executeCommands(option.actions, currentState);
    }

    const targetId = resolveChoiceEntry(scenario, option);
    if (!targetId) {
      const nextId = resolveNextSceneId(scenario, choiceSceneId);
      if (!nextId) return { error: 'Choice option has no branch and no next scene' };
      return SceneExecutor.navigateToScene(scenario, currentState, nextId);
    }

    return SceneExecutor.navigateToScene(scenario, currentState, targetId);
  }

  static advanceScene(
    scenario: Scenario,
    gameState: GameState
  ): { gameState: GameState; scene: Scene | null } {
    const currentId = gameState.currentSceneId;
    const nextId = resolveNextSceneId(scenario, currentId);
    if (!nextId) {
      return { gameState, scene: getSceneById(scenario, currentId) };
    }
    const result = SceneExecutor.navigateToScene(scenario, gameState, nextId);
    if ('error' in result) {
      return { gameState, scene: getSceneById(scenario, currentId) };
    }
    return result;
  }

  /** @deprecated Use advanceScene */
  static advanceContent(
    _scene: Scene,
    gameState: GameState,
    scenario?: Scenario
  ): { gameState: GameState; autoAdvance: boolean } {
    if (!scenario) {
      return { gameState, autoAdvance: false };
    }
    const result = SceneExecutor.advanceScene(scenario, gameState);
    return { gameState: result.gameState, autoAdvance: false };
  }

  static calculateProgress(scenario: Scenario, sceneId: string): SceneProgress {
    const progress = calculateScenarioProgress(scenario, sceneId);
    if (!progress) {
      return { currentIndex: 0, totalItems: scenario.scenes.length, percentage: 0 };
    }
    return progress;
  }

  static getAvailableChoices(
    choiceScene: ChoiceScene,
    gameState: GameState,
    evaluateCondition: (condition: string, state: GameState) => boolean
  ): ChoiceOption[] {
    return choiceScene.options.filter((option) => {
      if (!option.condition) return true;
      try {
        return evaluateCondition(option.condition, gameState);
      } catch (error) {
        console.error(`Error evaluating condition: ${option.condition}`, error);
        return false;
      }
    });
  }

  static hasMoreContent(_scene: Scene, _dialogueIndex?: number): boolean {
    return false;
  }

  static isAtEnd(scenario: Scenario, sceneId: string): boolean {
    return resolveNextSceneId(scenario, sceneId) === null;
  }

  static syncPhoneState(scenario: Scenario, gameState: GameState): GameState {
    return syncPhoneState(scenario, gameState);
  }

  static resolvePhoneStateTarget(scenario: Scenario, sceneId: string): 0 | 1 | 2 {
    return resolvePhoneStateTarget(scenario, sceneId);
  }

  static getScenePhoneCall(scene: Scene | null): PhoneCallScene | null {
    return scene?.type === 'phoneCall' ? scene : null;
  }

  static shouldShowScenePhoneCall(scene: Scene | null): boolean {
    return scene?.type === 'phoneCall';
  }

  static findLocatedScene(scenario: Scenario, sceneId: string) {
    return findLocatedScene(scenario.scenes, sceneId);
  }
}
